import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CurrencyDollar } from "@phosphor-icons/react";
import GlobalSearch from "@/components/GlobalSearch";
import StockOnHand from "@/pages/StockOnHand";

function StatBox({ label, value, sub, accent, to }) {
  const inner = (
    <>
      <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">{label}</div>
      <div className={`font-mono text-3xl font-semibold mt-2 tabular-nums ${accent || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </>
  );
  const cls = "p-6 border-r border-slate-200 last:border-r-0 block";
  const testId = `stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
  if (to) {
    return <Link to={to} className={`${cls} hover:bg-slate-50 transition-colors`} data-testid={testId}>{inner}</Link>;
  }
  return <div className={cls} data-testid={testId}>{inner}</div>;
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const { user, isViewer, canSeePricing, maskMoney } = useAuth();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  if (!stats) return <div className="p-8 text-slate-500">Loading…</div>;

  if (isViewer) {
    return (
      <div className="p-8 max-w-[1500px]" data-testid="dashboard-viewer">
        <header className="mb-8">
          <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">Welcome back, {user?.name}</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
        </header>
        <StockOnHand embedded />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="text-xs font-mono tracking-[0.2em] uppercase text-slate-500 mb-2">
          Welcome back, {user?.name}
        </div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-slate-900">Dashboard</h1>
      </header>

      {/* Quick search */}
      <div className="mb-8 max-w-2xl">
        <GlobalSearch placeholder="Search by brand, series, color or SKU — e.g. 3M 2080 Gloss Black" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 border border-slate-200 bg-white mb-8" data-testid="dashboard-stats">
        <StatBox label="Incoming" value={(stats.incoming_rolls || 0) + (stats.incoming_expected || 0)}
          sub={`${stats.pending_pos || 0} purchase order${(stats.pending_pos || 0) === 1 ? "" : "s"} · ${stats.incoming_rolls || 0} in transit`}
          to="/incoming-stock" />
        <StatBox label="Alerts" value={stats.low_stock.length + stats.expiring_soon.length + stats.expired.length}
          sub="need attention"
          accent={(stats.low_stock.length + stats.expiring_soon.length + stats.expired.length) > 0 ? "text-red-600" : ""}
          to="/alerts" />
        <StatBox label="Low Stock" value={stats.low_stock.length} sub="below threshold"
          accent={stats.low_stock.length > 0 ? "text-red-600" : ""} to="/alerts" />
        <StatBox label="Expiring ≤ 90d" value={stats.expiring_soon.length} sub={`${stats.expired.length} expired`}
          accent={stats.expiring_soon.length > 0 ? "text-amber-600" : ""} to="/alerts" />
      </div>

      {canSeePricing && stats.total_value != null && (
        <div className="border border-slate-200 bg-white p-6 mb-8 flex items-center gap-6">
          <div className="w-12 h-12 grid place-items-center bg-blue-50 border border-blue-200 rounded-md">
            <CurrencyDollar size={22} className="text-blue-600" />
          </div>
          <div>
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-slate-500">Estimated Inventory Value (Sell)</div>
            <div className="font-mono text-3xl font-semibold text-slate-900 mt-1" data-testid="dashboard-total-value">{maskMoney(`$${stats.total_value.toLocaleString()}`)}</div>
          </div>
        </div>
      )}

      <div className="pt-8 border-t border-slate-200">
        <StockOnHand embedded />
      </div>
    </div>
  );
}
